import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type { StatisticSummaryItemProps } from '@/tools/viewStatisticSummaries/StatisticSummaryItem.organism';

export interface ViewStatisticSummariesState {
  /** The statistic summary items loaded so far, across all fetched pages. */
  items: StatisticSummaryItemProps[];
  /** Whether a search/page request is in flight. */
  loading: boolean;
  /** Whether more items are available to load via infinite scroll. */
  hasNextPage: boolean;
  /** The name substring/pattern currently used to filter results. */
  nameFilter: string;
  /** Whether `nameFilter` must match the full name instead of a substring. */
  exactMatch: boolean;
  /** Whether the search is scoped to the current workbook only. */
  onlyInWorkbook: boolean;
}

export interface ViewStatisticSummariesActions {
  /** Merges a page of newly fetched items (deduped by id) and updates pagination state. */
  setItems: (items: StatisticSummaryItemProps[], hasNextPage: boolean) => void;
  /** Sets whether a search/page request is in flight. */
  setLoading: (loading: boolean) => void;
  /** Removes the item with the given id from the loaded list. */
  deleteItem: (id: string) => void;
  /** Adds a single item back into the list, keeping items sorted by name. */
  addItem: (item: StatisticSummaryItemProps) => void;
  /** Sets the name substring/pattern used to filter results. */
  setNameFilter: (name: string) => void;
  /** Sets whether `nameFilter` must match the full name instead of a substring. */
  setExactMatch: (isExact: boolean) => void;
  /** Sets whether the search is scoped to the current workbook only. */
  setOnlyInWorkbook: (onlyInWorkbook: boolean) => void;
  /** Restores the filter fields to their defaults, without touching the loaded items. */
  resetFilters: () => void;
  /** Clears the loaded items so a fresh search can repopulate from the first page. */
  resetItems: () => void;
  /** Restores the full store state to its defaults. */
  reset: () => void;
}

const initialState: ViewStatisticSummariesState = Object.freeze({
  items: [],
  loading: false,
  hasNextPage: false,
  nameFilter: '',
  exactMatch: false,
  onlyInWorkbook: true,
});

const viewStatisticSummariesActionsFactory: ActionsFactory<
  ViewStatisticSummariesState,
  ViewStatisticSummariesActions
> = (set, _get, resetState) => ({
  setItems: (items, hasNextPage) =>
    set((draft) => {
      const seenIds = new Set(draft.items.map((item) => item.id));
      const newItems = items.filter((item) => {
        if (seenIds.has(item.id)) {
          return false;
        }
        seenIds.add(item.id);
        return true;
      });
      draft.items = [...draft.items, ...newItems];
      draft.hasNextPage = hasNextPage;
    }),

  setLoading: (loading) =>
    set((draft) => {
      draft.loading = loading;
    }),

  deleteItem: (id) =>
    set((draft) => {
      draft.items = draft.items.filter((item) => item.id !== id);
    }),

  addItem: (item) =>
    set((draft) => {
      draft.items = [...draft.items, item].sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
    }),

  setNameFilter: (name) =>
    set((draft) => {
      draft.nameFilter = name;
    }),

  setExactMatch: (isExact) =>
    set((draft) => {
      draft.exactMatch = isExact;
    }),

  setOnlyInWorkbook: (onlyInWorkbook) =>
    set((draft) => {
      draft.onlyInWorkbook = onlyInWorkbook;
    }),

  resetFilters: () =>
    set((draft) => {
      draft.nameFilter = initialState.nameFilter;
      draft.exactMatch = initialState.exactMatch;
      draft.onlyInWorkbook = initialState.onlyInWorkbook;
    }),

  resetItems: () =>
    set((draft) => {
      draft.items = [];
      draft.hasNextPage = true;
    }),

  reset: resetState,
});

const STORE_NAME = 'ViewStatisticSummaries';

const [ViewStatisticSummariesStoreProvider, useViewStatisticSummariesStoreInstance, useViewStatisticSummariesSelector] =
  createStore(STORE_NAME, initialState, viewStatisticSummariesActionsFactory);

export { ViewStatisticSummariesStoreProvider };

export const useViewStatisticSummariesItems = () => useViewStatisticSummariesSelector((state) => state.items);
export const useViewStatisticSummariesLoading = () => useViewStatisticSummariesSelector((state) => state.loading);
export const useViewStatisticSummariesHasNextPage = () =>
  useViewStatisticSummariesSelector((state) => state.hasNextPage);
export const useViewStatisticSummariesNameFilter = () => useViewStatisticSummariesSelector((state) => state.nameFilter);
export const useViewStatisticSummariesExactMatch = () => useViewStatisticSummariesSelector((state) => state.exactMatch);
export const useViewStatisticSummariesOnlyInWorkbook = () =>
  useViewStatisticSummariesSelector((state) => state.onlyInWorkbook);

export const useViewStatisticSummariesActions = () => useViewStatisticSummariesStoreInstance().actions;

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. Prefer
 * `<TestStoreProvider>` or `<*StoreProvider>` with UI-driven assertions instead.
 */
export function createTestViewStatisticSummariesStore(overrides?: Partial<ViewStatisticSummariesState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestViewStatisticSummariesStore() should only be used in tests.');
  }

  return createStoreInstance({ ...initialState, ...overrides }, viewStatisticSummariesActionsFactory);
}

/**
 * @deprecated Legacy singleton bridge for non-React consumers (action files, utilities)
 * that cannot use hooks. New code should use the hooks above and the Provider.
 */
export function legacyCreateViewStatisticSummariesStore() {
  return createStoreInstance<ViewStatisticSummariesState, ViewStatisticSummariesActions>(
    initialState,
    viewStatisticSummariesActionsFactory,
    { storeName: STORE_NAME },
  );
}
